import { useEffect } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * Scroll-Verhalten bei Route-Wechsel.
 *
 *  - Ohne Hash: springt zum Seiten-Anfang (nur bei PUSH-Navigation, damit
 *    Browser-Back/Forward die native Restoration behält).
 *  - Mit Hash (`#kontakt`, `#use-cases` …): scrollt sanft zum Element sobald
 *    es im DOM verfügbar ist. Nötig für SPA-Nav aus fremden Routen wie
 *    `/erleben` → `/#kontakt`: die Zielsektion ist beim Route-Wechsel noch
 *    nicht gemountet (CMS-Fetch + Render laufen). Wir retryen bis 3 s.
 */
export default function ScrollToTop() {
    const { pathname, hash, state } = useLocation() as {
        pathname: string;
        hash: string;
        state: { anchor?: string } | null;
    };
    const navType = useNavigationType();

    useEffect(() => {
        // Anchor kann via Hash ODER via location.state kommen. Der Header
        // nutzt navigate("/", { state: { anchor } }) für SPA-Cross-Route-Sprünge,
        // damit KEIN Hard-Reload und KEIN nativer Anchor-Jump passiert.
        const stateAnchor = state && typeof state.anchor === "string" ? state.anchor : "";
        const hashId = hash ? hash.replace(/^#/, "") : "";
        const targetId = stateAnchor || hashId;

        if (!targetId) {
            if (navType !== "POP") {
                window.scrollTo({ top: 0, left: 0, behavior: "auto" });
            }
            return;
        }
        let attempts = 0;
        let cancelled = false;
        const tick = () => {
            if (cancelled) return;
            const el = document.getElementById(targetId);
            if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "start" });
                return;
            }
            if (++attempts > 30) return; // ~3 s @ 100 ms
            window.setTimeout(tick, 100);
        };
        // Erster Versuch nach dem Layout-Commit.
        window.requestAnimationFrame(tick);
        return () => { cancelled = true; };
    }, [pathname, hash, state, navType]);

    return null;
}
